import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    ChevronRightIcon,
    CircleCheckIcon,
    CircleOffIcon,
    GripVerticalIcon,
    ImageIcon,
    LinkIcon,
    MonitorIcon,
    PlusIcon,
    SmartphoneIcon,
    UnlinkIcon,
} from 'lucide-react';
import { Reorder, useDragControls } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import { toImageSrc } from '@/services/imageSrc';
import server from '@/services/server';
import ClubEmptyList from './ClubEmptyList';
import ClubImageUpload from './ClubImageUpload';
import ClubLinkDestinationFields from './ClubLinkDestinationFields';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const EMPTY_LINK = /^(https?:\/\/)?$/i;

function bannerHasLink(item) {
    const dest = String(item?.link || item?.mobileLink || '').trim();
    if (!dest || EMPTY_LINK.test(dest)) return false;
    const type = String(item?.type || '').trim();
    return !type || type === 'link' || type === 'category' || type === 'advertiser';
}

function bannerLinkTip(item) {
    if (!bannerHasLink(item)) return 'Sem link';
    const type = String(item?.type || '').trim();
    const dest = String(item?.link || item?.mobileLink || '').trim();
    if (type === 'category') return dest ? `Abre a categoria ${dest}` : 'Abre uma categoria';
    if (type === 'advertiser') return dest ? `Abre o anunciante ${dest}` : 'Abre um anunciante';
    return dest;
}

function BannerMetaTip({ tip, className, children }) {
    return (
        <Tooltip>
            <TooltipTrigger
                type="button"
                aria-label={tip}
                className={cn(
                    'inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground',
                    className,
                )}
            >
                {children}
            </TooltipTrigger>
            <TooltipContent>{tip}</TooltipContent>
        </Tooltip>
    );
}

const bannerSchema = z.object({
    title: z.string().min(1, 'Nome é obrigatório'),
    link: z.string().optional(),
    type: z.string().optional(),
    isMobile: z.boolean(),
    image: z.string().trim().min(1, 'Imagem é obrigatória'),
    status: z.boolean(),
});

function BannerForm({ data, mutate, open, onOpenChange, loading = false }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(bannerSchema),
        defaultValues: {
            title: '',
            link: 'https://',
            type: 'link',
            isMobile: false,
            image: '',
            status: true,
        },
    });

    useEffect(() => {
        if (!open) return;

        if (data) {
            form.reset({
                title: data.title || '',
                link: data.link || 'https://',
                type: data.type || 'link',
                isMobile: Boolean(data.isMobile),
                image: data.image || '',
                status: data.status !== false,
            });
        } else {
            form.reset({
                title: '',
                link: 'https://',
                type: 'link',
                isMobile: false,
                image: '',
                status: true,
            });
        }
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            if (!data?.id) {
                await server.post('club/settings/banners', values);
                enqueueSnackbar('Banner adicionado');
            } else {
                await server.put(`club/settings/banners/${data.id}`, values);
                enqueueSnackbar('Banner alterado');
            }

            await mutate();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const onDelete = async () => {
        if (!data?.id) return;

        try {
            await confirm({
                title: 'Remover banner',
                description: 'Você confirma que está removendo o banner?',
                confirmationText: 'Excluir',
                confirmationButtonProps: { variant: 'destructive' },
            });

            await server.delete(`club/settings/banners/${data.id}`);
            enqueueSnackbar('Banner removido');
            await mutate();
            onOpenChange(false);
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;
    const isMobile = form.watch('isMobile');
    const imagePreview = form.watch('image');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-lg">
                <ScrollDialogHeader>
                    <DialogTitle>{data?.id ? 'Editar banner' : 'Adicionar banner'}</DialogTitle>
                </ScrollDialogHeader>

                {loading ? (
                    <DialogBody>
                        <div className="flex justify-center py-10">
                            <Spinner className="size-6" />
                        </div>
                    </DialogBody>
                ) : (
                    <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                        <DialogBody>
                            <FieldGroup>
                                <Field>
                                    <FieldLabel htmlFor="banner-title">Nome do banner</FieldLabel>
                                    <Controller
                                        name="title"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Input id="banner-title" {...field} value={field.value ?? ''} />
                                        )}
                                    />
                                </Field>

                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Onde o banner aparece?</CardTitle>
                                        <CardDescription>Defina se ele aparece no app ou no site.</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => form.setValue('isMobile', false, { shouldDirty: true })}
                                                className={cn(
                                                    'flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors',
                                                    !isMobile
                                                        ? 'border-primary bg-primary/5 shadow-sm'
                                                        : 'border-border hover:bg-muted/50',
                                                )}
                                            >
                                                <MonitorIcon
                                                    className={cn(
                                                        'size-9',
                                                        !isMobile ? 'text-primary' : 'text-muted-foreground',
                                                    )}
                                                />
                                                <span
                                                    className={cn(
                                                        'text-sm font-semibold',
                                                        !isMobile ? 'text-primary' : 'text-muted-foreground',
                                                    )}
                                                >
                                                    Web
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => form.setValue('isMobile', true, { shouldDirty: true })}
                                                className={cn(
                                                    'flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors',
                                                    isMobile
                                                        ? 'border-primary bg-primary/5 shadow-sm'
                                                        : 'border-border hover:bg-muted/50',
                                                )}
                                            >
                                                <SmartphoneIcon
                                                    className={cn(
                                                        'size-9',
                                                        isMobile ? 'text-primary' : 'text-muted-foreground',
                                                    )}
                                                />
                                                <span
                                                    className={cn(
                                                        'text-sm font-semibold',
                                                        isMobile ? 'text-primary' : 'text-muted-foreground',
                                                    )}
                                                >
                                                    App
                                                </span>
                                            </button>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Imagem do banner</CardTitle>
                                        <CardDescription>
                                            {isMobile ? 'Dimensões: 300×150 px' : 'Dimensões: 1000×300 px'} — obrigatória
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <ClubImageUpload
                                            name="image"
                                            required
                                            onChange={(_, value) =>
                                                form.setValue('image', value ?? '', {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                })
                                            }
                                            value={imagePreview}
                                            label={
                                                isMobile
                                                    ? 'Dimensões obrigatórias: 300x150 pixels'
                                                    : 'Dimensões obrigatórias: 1000x300 pixels'
                                            }
                                            canvaWidth="100%"
                                            canvaHeight={isMobile ? 200 : 150}
                                        />
                                        {form.formState.errors.image?.message ? (
                                            <FieldError>{form.formState.errors.image.message}</FieldError>
                                        ) : null}
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Banner clicável</CardTitle>
                                        <CardDescription>Defina o destino do link do banner.</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <ClubLinkDestinationFields
                                            control={form.control}
                                            setValue={form.setValue}
                                            watch={form.watch}
                                            includeEmptyOption
                                        />
                                    </CardContent>
                                </Card>

                                <Field orientation="horizontal">
                                    <FieldLabel htmlFor="banner-status">Status do banner</FieldLabel>
                                    <Controller
                                        name="status"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Switch id="banner-status" checked={field.value} onCheckedChange={field.onChange} />
                                        )}
                                    />
                                </Field>
                            </FieldGroup>
                        </DialogBody>

                        <ScrollDialogFooter>
                            {data?.id ? (
                                <Button type="button" variant="destructive" onClick={onDelete} disabled={isSubmitting}>
                                    Excluir
                                </Button>
                            ) : null}
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                            </Button>
                        </ScrollDialogFooter>
                    </form>
                )}
            </ScrollDialogContent>
        </Dialog>
    );
}

function DialogBannerAdd({ mutate }) {
    const { id, open, handleClose } = useDialog('#banners');
    const { data, isFetching } = useFetch(open && id ? `club/settings/banners/${id}` : null);

    if (!open) return null;

    return (
        <BannerForm
            data={id ? data : undefined}
            mutate={mutate}
            open={open}
            loading={Boolean(id && isFetching)}
            onOpenChange={isOpen => !isOpen && handleClose()}
        />
    );
}

function ListBannersItem({ item, onDragEnd }) {
    const controls = useDragControls();
    const isMobile = Boolean(item.isMobile);
    const isActive = item.status !== false;
    const hasLink = bannerHasLink(item);

    return (
        <Reorder.Item
            value={item.id}
            dragListener={false}
            dragControls={controls}
            onDragEnd={onDragEnd}
            className="flex items-center gap-2 border-b bg-background p-3 last:border-b-0"
        >
            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="cursor-grab active:cursor-grabbing"
                onPointerDown={event => controls.start(event)}
            >
                <GripVerticalIcon className="size-4" />
            </Button>
            <img src={toImageSrc(item.image)} alt="" className="h-10 w-[90px] rounded-md object-cover" />
            <span
                className={cn(
                    'min-w-0 flex-1 truncate text-sm font-medium',
                    !isActive && 'text-muted-foreground',
                )}
            >
                {item.title}
            </span>
            <div className="flex shrink-0 items-center">
                <BannerMetaTip tip={isMobile ? 'Aparece no aplicativo' : 'Aparece no site'}>
                    {isMobile ? <SmartphoneIcon className="size-4" /> : <MonitorIcon className="size-4" />}
                </BannerMetaTip>
                <BannerMetaTip tip={bannerLinkTip(item)}>
                    {hasLink ? <LinkIcon className="size-4" /> : <UnlinkIcon className="size-4" />}
                </BannerMetaTip>
                <BannerMetaTip
                    tip={isActive ? 'Ativo — visível para o assinante' : 'Inativo — oculto para o assinante'}
                    className={isActive ? 'text-primary' : 'text-muted-foreground'}
                >
                    {isActive ? <CircleCheckIcon className="size-4" /> : <CircleOffIcon className="size-4" />}
                </BannerMetaTip>
            </div>
            <Button variant="ghost" size="icon-sm" render={<Link to={`#banners|${item.id}`} title="Abrir" />}>
                <ChevronRightIcon className="size-4" />
            </Button>
        </Reorder.Item>
    );
}

function ListBanners({ data, mutate, onReorderSave }) {
    const handleReorder = useCallback(
        newOrder => {
            const reordered = newOrder
                .map((id, index) => {
                    const item = data?.find(entry => entry.id === id);
                    return item ? { ...item, position: index } : null;
                })
                .filter(Boolean);
            mutate({ data: reordered }, false);
        },
        [data, mutate],
    );

    const handleDragEnd = useCallback(() => {
        onReorderSave(data?.map(item => item.id));
    }, [data, onReorderSave]);

    if (!data?.length) {
        return <ClubEmptyList />;
    }

    return (
        <Reorder.Group axis="y" values={data.map(item => item.id)} onReorder={handleReorder} className="flex flex-col">
            {data.map(item => (
                <ListBannersItem key={item.id} item={item} onDragEnd={handleDragEnd} />
            ))}
        </Reorder.Group>
    );
}

export default function BannersClubCard() {
    const { data, mutate } = useFetch('club/settings/banners');
    const { enqueueSnackbar } = useClubErrors();
    const { open: manageOpen, handleClose: closeManage } = useDialog('#banners-manage');
    const portalContainer = useClubPortalContainer();
    const count = data?.length ?? 0;
    const activeCount = data?.filter(item => item.status !== false).length ?? 0;

    async function handleSendReorder(reordered) {
        await server.post('club/settings/banners', { reorder: reordered });
        enqueueSnackbar('Itens reordenados');
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={ImageIcon}
                        title="Banners"
                        description="Personalize seus banners e configure seus destinos."
                    />
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                    <span className="text-muted-foreground">Cadastrados</span>
                    <p className="font-medium">
                        {count === 0 ? 'Nenhum banner' : `${count} banner${count !== 1 ? 's' : ''}`}
                    </p>
                    {count > 0 ? <p className="text-muted-foreground">{activeCount} ativo(s)</p> : null}
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#banners-manage" />}>
                        Gerenciar banners
                    </Button>
                </CardFooter>
            </Card>

            <Dialog open={manageOpen} onOpenChange={isOpen => !isOpen && closeManage()}>
                <ScrollDialogContent container={portalContainer} className="sm:max-w-2xl">
                    <ScrollDialogHeader>
                        <DialogTitle>Banners</DialogTitle>
                    </ScrollDialogHeader>
                    <DialogBody>
                        <div className="mb-4 flex justify-end">
                            <Button variant="outline" size="sm" render={<Link to="#banners" />}>
                                <PlusIcon className="size-4" />
                                Adicionar banner
                            </Button>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <ListBanners data={data || []} mutate={mutate} onReorderSave={handleSendReorder} />
                            </CardContent>
                        </Card>
                    </DialogBody>
                    <ScrollDialogFooter>
                        <Button variant="outline" onClick={closeManage}>
                            Fechar
                        </Button>
                    </ScrollDialogFooter>
                </ScrollDialogContent>
            </Dialog>

            <DialogBannerAdd mutate={mutate} />
        </>
    );
}
